import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {transaction} from '@datorama/akita';
import {CookieService} from 'ngx-cookie-service';
import {User} from '../../models/user.model';
import {AuthRedirectService} from '../../services/auth-redirect.service';
import {SessionStore} from './session.store';
import {environment} from '@env/environment';
import {SessionQuery} from './session.query';
import {from, Observable, throwError, timer} from 'rxjs';
import {mergeMap, retryWhen} from 'rxjs/operators';
import {KeycloakService} from 'keycloak-angular';
import {
  LOAD_USER_RETRY_DELAYS_MS,
  LOGIN_REDIRECT_KEY,
  LOGIN_REDIRECT_LOOP_WINDOW_MS,
} from '../../constants/token-refresh.const';

@Injectable()
export class SessionService {
  private loginRedirectInProgress = false;

  constructor(
    private sessionStore: SessionStore,
    private sessionQuery: SessionQuery,
    private http: HttpClient,
    private cookieService: CookieService,
    private authRedirectService: AuthRedirectService,
    private keycloak: KeycloakService,
  ) {
  }

  @transaction()

  public saveToken(token: string) {
    this.sessionStore.update({token});
    this.cookieService.set(environment.tokenKey, token, undefined, '/');
  }

  clearToken() {
    this.sessionStore.update({token: null});
    this.cookieService.delete(environment.tokenKey);
  }

  public getToken(): string {
    return this.sessionQuery.getValue().token || '';
  }

  public loadUser(): void {
    this.sessionStore.setLoading(true);
    this.http.get(`${environment.apiRoot}/profiling/api/account`)
      .pipe(
        // Мережеві / 5xx помилки (напр. одразу після пробудження ноутбука) — пробуємо ще раз.
        retryWhen(errors => errors.pipe(
          mergeMap((error: HttpErrorResponse, attempt: number) =>
            attempt < LOAD_USER_RETRY_DELAYS_MS.length && this.isTransientError(error)
              ? timer(LOAD_USER_RETRY_DELAYS_MS[attempt])
              : throwError(error),
          ),
        )),
      )
      .subscribe(
        (user: User) => {
          this.clearLoginRedirectMark();
          this.sessionStore.update({
            user,
            loading: false,
          });
        },
        (error: HttpErrorResponse) => {
          console.error('[Auth] Не вдалося завантажити профіль користувача', error);
          // loading=false → AuthGuard побачить, що user відсутній, і відправить на логін.
          this.sessionStore.setLoading(false);
        },
      );
  }

  /**
   * Редірект на логін Keycloak без знищення SSO-сесії (на відміну від logout()).
   * Перший раз — звичайний login(): при живій сесії користувач повернеться без пароля.
   * Якщо редірект повторюється протягом LOGIN_REDIRECT_LOOP_WINDOW_MS (напр. /account постійно падає),
   * примусово показуємо форму входу, щоб не зациклити сторінку на тихих редіректах.
   */
  public redirectToLogin(): void {
    // canActivate і canActivateChild можуть викликати це одночасно — редіректимо один раз.
    if (this.loginRedirectInProgress) {
      return;
    }
    this.loginRedirectInProgress = true;

    const now = Date.now();
    const lastRedirect = Number(this.readLoginRedirectMark()) || 0;
    const isRepeated = now - lastRedirect < LOGIN_REDIRECT_LOOP_WINDOW_MS;

    this.writeLoginRedirectMark(String(now));
    this.sessionStore.update({token: null, user: null});

    const options: { redirectUri: string; prompt?: 'none' | 'login' } = {redirectUri: window.location.href};
    if (isRepeated) {
      options.prompt = 'login';
    }

    this.keycloak.login(options);
  }

  /** Явний вихід користувача (кнопка «Вийти»). Знищує SSO-сесію в Keycloak. */
  public logout(): Observable<any> {
    this.clearToken();
    this.sessionStore.reset();
    return from(this.keycloak.logout());
  }

  private isTransientError(error: HttpErrorResponse): boolean {
    return !!error && (error.status === 0 || error.status >= 500);
  }

  private readLoginRedirectMark(): string | null {
    try {
      return sessionStorage.getItem(LOGIN_REDIRECT_KEY);
    } catch {
      return null;
    }
  }

  private writeLoginRedirectMark(value: string): void {
    try {
      sessionStorage.setItem(LOGIN_REDIRECT_KEY, value);
    } catch {
    }
  }

  private clearLoginRedirectMark(): void {
    try {
      sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
    } catch {
    }
  }
}
